import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useOrganization } from '@clerk/react';
import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import { stepHeadline } from '@/containers/playbooks/custom-builder/connectors';
import {
  HoldDecisionCancelled,
  holdsBody,
} from '@/containers/playbooks/custom-builder/held-records';
import { graphTrigger } from '@/containers/playbooks/custom-builder/test-record.util';
import { useHeldRecordsDecision } from '@/containers/playbooks/custom-builder/use-held-records-decision.hook';
import {
  useAcceptPlayProposalMutation,
  useCachedAgent,
  useDiscardPlayProposalMutation,
} from '@/store/features/gtm-agents.feature';
import type { CustomGraph } from '@/types/custom-playbooks';
import type { PlayProposalDto } from '@/types/gtm-agents';
import { addedAgentPlay } from './agent-plays.util';
import { proposalIsBlocked } from './play-proposal-review.util';
import { serverReason } from './server-reason.util';
import { isConflict, STALE_PROPOSAL } from './use-play-proposal.hook';
import { useRefreshAgentChat } from './use-refresh-agent-chat.hook';
import { consentBody, useReviewCard } from './use-review-card.hook';

/**
 * Accept and Discard on Clu's proposal card in the chat, through the same
 * endpoints and held-records decision as the canvas. Accept saves the
 * proposal as the play and starts Clu's check in the chat it was proposed
 * in; Discard drops it. Both post their line into that chat, which is read
 * again. An open canvas reads the proposal again and follows (the mutations
 * invalidate it). Nothing runs unless the proposal is pending, and Accept
 * never runs for a blocked one.
 */
export function useChatProposalActions(
  agentId: string | undefined,
  proposal: PlayProposalDto | undefined
) {
  const agent = useCachedAgent(agentId);
  // The agent's org, else the open one (the proposal was read in it).
  const { organization } = useOrganization();
  const organizationId = agent?.organizationId ?? organization?.id;
  const enqueueAcceptedReview =
    useCopilotRuntimeOptional()?.enqueueAcceptedPlayReview;
  const refreshChat = useRefreshAgentChat();
  const [accept] = useAcceptPlayProposalMutation();
  const [discard] = useDiscardPlayProposalMutation();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);

  const graph = proposal?.graph as CustomGraph | undefined;
  const trigger = graphTrigger(graph);
  const held = useHeldRecordsDecision(
    `chat:${agentId ?? ''}:${proposal?.id ?? ''}`,
    Boolean(agentId && proposal),
    Object.fromEntries([
      ...(graph?.nodes ?? []).map((node) => [
        node.id,
        stepHeadline(node, trigger),
      ]),
      ...(proposal?.removed ?? []).map((node) => [node.id, node.headline]),
    ])
  );
  const runHeld = held.run;

  const open = proposal?.status === 'pending';
  const review = useReviewCard(proposal);
  const reviewCard = review.card;
  const canAccept = Boolean(
    open && proposal && !proposalIsBlocked(proposal) && !review.held
  );

  const settle = useCallback(
    // Whether the action went through: false when it failed, was cancelled
    // or another was already running.
    async (action: () => Promise<void>, fallback: string) => {
      if (pending.current) return false;
      pending.current = true;
      setBusy(true);
      try {
        await action();
        return true;
      } catch (error) {
        if (!(error instanceof HoldDecisionCancelled))
          toast.error(
            isConflict(error) ? STALE_PROPOSAL : serverReason(error, fallback)
          );
        return false;
      } finally {
        pending.current = false;
        setBusy(false);
      }
    },
    []
  );

  const onAccept = useCallback(async () => {
    if (!agentId || !proposal || !canAccept) return false;
    return settle(async () => {
      await runHeld(async (holds) => {
        const saved = await accept({
          agentId,
          proposalId: proposal.id,
          ...(organizationId ? { organizationId } : {}),
          ...holdsBody(holds),
          ...consentBody(reviewCard),
        }).unwrap();
        // Inside the action, as on the canvas: the play is saved even if the
        // card unmounts before the held-records run settles.
        const chatId = proposal.conversationId ?? null;
        const queued =
          chatId && organizationId
            ? enqueueAcceptedReview?.({
                organizationId,
                agentId,
                conversationId: chatId,
                proposalId: proposal.id,
                playId:
                  proposal.playbookId ??
                  (agent && addedAgentPlay(agent, saved)) ??
                  undefined,
              })
            : false;
        if (!queued) refreshChat(chatId);
        return saved;
      }, proposal.holds_affected);
    }, 'Could not accept the proposal.');
  }, [
    agentId,
    agent,
    organizationId,
    proposal,
    canAccept,
    reviewCard,
    settle,
    runHeld,
    accept,
    enqueueAcceptedReview,
    refreshChat,
  ]);

  const onDiscard = useCallback(async () => {
    if (!agentId || !proposal || !open) return;
    await settle(async () => {
      await discard({
        agentId,
        proposalId: proposal.id,
        ...(organizationId ? { organizationId } : {}),
      }).unwrap();
      refreshChat(proposal.conversationId ?? null);
    }, 'Could not discard the proposal.');
  }, [agentId, organizationId, proposal, open, settle, discard, refreshChat]);

  return {
    open,
    canAccept,
    reviewCard,
    busy,
    dialog: held.dialog,
    onAccept,
    onDiscard: () => void onDiscard(),
  };
}
